import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router';
import { verifyPasswordResetCode, confirmPasswordReset, applyActionCode } from 'firebase/auth';
import { auth } from '@shared/services/firebase';
import { Icons } from '@shared/constants';

/**
 * Handles Firebase auth action links (password reset, email verification, email change).
 * Firebase uses a single action URL for all modes: /__/auth/action?mode=...&oobCode=...
 */
export const PasswordResetAction: React.FC = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const mode = searchParams.get('mode') || '';
    const oobCode = searchParams.get('oobCode') || '';

    const [status, setStatus] = useState<'verifying' | 'ready' | 'success' | 'error'>('verifying');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPw, setConfirmPw] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Handle action code on mount based on mode
    useEffect(() => {
        if (!oobCode) {
            setStatus('error');
            setErrorMessage('Invalid or missing link. Please request a new one.');
            return;
        }

        if (mode === 'verifyEmail') {
            // Email verification — just apply the code
            applyActionCode(auth, oobCode)
                .then(() => setStatus('success'))
                .catch((err) => {
                    setStatus('error');
                    if (err.code === 'auth/expired-action-code') {
                        setErrorMessage('This verification link has expired. Please request a new one.');
                    } else if (err.code === 'auth/invalid-action-code') {
                        setErrorMessage('This verification link is invalid or has already been used.');
                    } else {
                        setErrorMessage('Something went wrong verifying your email.');
                    }
                });
        } else if (mode === 'resetPassword') {
            // Password reset — verify code first, then show new password form
            verifyPasswordResetCode(auth, oobCode)
                .then((userEmail) => {
                    setEmail(userEmail);
                    setStatus('ready');
                })
                .catch((err) => {
                    setStatus('error');
                    if (err.code === 'auth/expired-action-code') {
                        setErrorMessage('This reset link has expired. Please request a new one.');
                    } else if (err.code === 'auth/invalid-action-code') {
                        setErrorMessage('This reset link is invalid or has already been used.');
                    } else {
                        setErrorMessage('Something went wrong verifying your reset link.');
                    }
                });
        } else if (mode === 'recoverEmail') {
            // Email change reversal — apply the code to revert
            applyActionCode(auth, oobCode)
                .then(() => setStatus('success'))
                .catch(() => {
                    setStatus('error');
                    setErrorMessage('Something went wrong recovering your email.');
                });
        } else {
            setStatus('error');
            setErrorMessage('Unrecognized action. Please try again from your email.');
        }
    }, [oobCode, mode]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (password.length < 6) {
            setErrorMessage('Password must be at least 6 characters.');
            return;
        }
        if (password !== confirmPw) {
            setErrorMessage('Passwords do not match.');
            return;
        }
        setIsSubmitting(true);
        setErrorMessage('');
        try {
            await confirmPasswordReset(auth, oobCode, password);
            setStatus('success');
        } catch (err: any) {
            if (err.code === 'auth/weak-password') {
                setErrorMessage('Password is too weak. Please choose a stronger one.');
            } else if (err.code === 'auth/expired-action-code') {
                setErrorMessage('This reset link has expired. Please request a new one.');
                setStatus('error');
            } else {
                setErrorMessage('Failed to reset password. Please try again.');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-sand flex flex-col items-center justify-center p-6 text-growth font-sans">
            <div className="w-full max-w-sm bg-white p-8 rounded-2xl shadow-xl border border-mist/20">
                <div className="text-center mb-8">
                    <img src="/assets/logos/enough_wordmark_green.png" alt="enough" className="w-50 h-auto mx-auto" />
                </div>

                {status === 'verifying' && (
                    <div className="text-center space-y-4">
                        <div className="w-12 h-12 border-4 border-mist border-t-foliage rounded-full animate-spin mx-auto" />
                        <p className="text-sm text-growth/70">
                            {mode === 'verifyEmail' ? 'Verifying your email...' :
                             mode === 'recoverEmail' ? 'Recovering your email...' :
                             'Verifying your reset link...'}
                        </p>
                    </div>
                )}

                {status === 'error' && (
                    <div className="text-center space-y-4">
                        <div className="w-12 h-12 bg-wildfire/10 rounded-full flex items-center justify-center mx-auto">
                            <Icons.AlertCircle className="w-6 h-6 text-wildfire" />
                        </div>
                        <p className="text-sm text-growth">{errorMessage}</p>
                        <button
                            onClick={() => navigate('/login', { replace: true })}
                            className="text-sm text-growth font-medium hover:underline"
                        >
                            Back to Sign In
                        </button>
                    </div>
                )}

                {status === 'ready' && (
                    <>
                        <p className="text-sm text-growth/70 mb-1 text-center">
                            Set a new password for
                        </p>
                        <p className="text-sm text-growth font-medium mb-6 text-center">{email}</p>

                        {errorMessage && (
                            <div className="mb-4 p-3 text-xs rounded-xl border bg-wildfire/10 text-wildfire border-wildfire/20">
                                {errorMessage}
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label htmlFor="new-password" className="block text-xs font-bold uppercase tracking-wider text-growth mb-1">
                                    New Password
                                </label>
                                <input
                                    id="new-password"
                                    type="password"
                                    autoComplete="new-password"
                                    required
                                    minLength={6}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="w-full bg-sand/30 border border-mist/20 rounded-xl px-4 py-3 text-growth focus:outline-hidden focus:border-growth/50 transition-colors"
                                    placeholder="At least 6 characters"
                                />
                            </div>
                            <div>
                                <label htmlFor="confirm-password" className="block text-xs font-bold uppercase tracking-wider text-growth mb-1">
                                    Confirm Password
                                </label>
                                <input
                                    id="confirm-password"
                                    type="password"
                                    autoComplete="new-password"
                                    required
                                    value={confirmPw}
                                    onChange={(e) => setConfirmPw(e.target.value)}
                                    className="w-full bg-sand/30 border border-mist/20 rounded-xl px-4 py-3 text-growth focus:outline-hidden focus:border-growth/50 transition-colors"
                                    placeholder="Re-enter your password"
                                />
                            </div>
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="w-full bg-growth text-cloud font-medium py-3 rounded-xl shadow-lg shadow-growth/20 hover:bg-growth-dark transition-all disabled:opacity-70 disabled:cursor-not-allowed"
                            >
                                {isSubmitting ? 'Resetting...' : 'Reset Password'}
                            </button>
                        </form>
                    </>
                )}

                {status === 'success' && (
                    <div className="text-center space-y-4">
                        <div className="w-12 h-12 bg-growth/10 rounded-full flex items-center justify-center mx-auto">
                            <Icons.Check className="w-6 h-6 text-growth" />
                        </div>
                        <p className="text-sm text-growth">
                            {mode === 'verifyEmail' && 'Your email has been verified.'}
                            {mode === 'resetPassword' && 'Your password has been reset successfully.'}
                            {mode === 'recoverEmail' && 'Your email has been recovered.'}
                        </p>
                        <button
                            onClick={() => navigate('/login', { replace: true })}
                            className="w-full bg-growth text-cloud font-medium py-3 rounded-xl shadow-lg shadow-growth/20 hover:bg-growth-dark transition-all"
                        >
                            Sign In
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};
